import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Chip, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Tabs, Tab, FormControlLabel, Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import api from '../api';
import { CampoCorreo, CampoTelefono } from './CamposContacto';
import { errorCorreo, errorTelefono } from '../contacto';
import { useEsMovil, tablaAncha } from '../estilos/responsivo';
import { CONTENEDOR_TABLA } from '../estilos/tarjeta';

const initialForm = {
  razon_social: '', nombre_comercial: '', tipo_documento: 'NIT',
  numero_documento: '', regimen_fiscal: '', direccion_fiscal: '',
  ciudad: '', departamento: '', pais: 'Colombia',
  email_facturacion: '', telefono: '', moneda_default: 'USD',
  nara_empresa_id: '',
  es_cliente: true, es_proveedor: false,
  banco: '', tipo_cuenta: '', numero_cuenta: '',
};

// Opciones por pais
const paisConfig = {
  Colombia: {
    tipoDoc: 'NIT',
    tipoDocLabel: 'NIT',
    regimenes: ['Simplificado', 'Comun', 'Gran Contribuyente', 'No Responsable de IVA'],
    moneda: 'COP',
  },
  Mexico: {
    tipoDoc: 'RFC',
    tipoDocLabel: 'RFC',
    regimenes: ['General de Ley', 'Incorporacion Fiscal', 'Actividad Empresarial', 'Honorarios', 'Arrendamiento'],
    moneda: 'MXN',
  },
  Panama: {
    tipoDoc: 'RUC',
    tipoDocLabel: 'RUC',
    regimenes: ['Persona Natural', 'Persona Juridica'],
    moneda: 'USD',
  },
};

const paises = ['Colombia', 'Mexico', 'Panama', 'Estados Unidos', 'UK', 'Otro'];

export default function Terceros() {
  const esMovil = useEsMovil();
  const [terceros, setTerceros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [tabRol, setTabRol] = useState(0); // 0=Todos, 1=Clientes, 2=Proveedores

  // Import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [empresasNara, setEmpresasNara] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [importMsg, setImportMsg] = useState('');

  const rolParam = tabRol === 1 ? 'cliente' : tabRol === 2 ? 'proveedor' : undefined;

  const fetchTerceros = async () => {
    try {
      const params = rolParam ? `?rol=${rolParam}` : '';
      const res = await api.get(`/terceros${params}`);
      setTerceros(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTerceros(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabRol]);

  const handleOpen = (tercero = null) => {
    if (tercero) {
      setEditId(tercero.id);
      setForm({
        razon_social: tercero.razon_social || '',
        nombre_comercial: tercero.nombre_comercial || '',
        tipo_documento: tercero.tipo_documento || 'NIT',
        numero_documento: tercero.numero_documento || '',
        regimen_fiscal: tercero.regimen_fiscal || '',
        direccion_fiscal: tercero.direccion_fiscal || '',
        ciudad: tercero.ciudad || '',
        departamento: tercero.departamento || '',
        pais: tercero.pais || 'Colombia',
        email_facturacion: tercero.email_facturacion || '',
        telefono: tercero.telefono || '',
        moneda_default: tercero.moneda_default || 'USD',
        nara_empresa_id: tercero.nara_empresa_id || '',
        es_cliente: tercero.es_cliente ?? true,
        es_proveedor: tercero.es_proveedor ?? false,
        banco: tercero.banco || '',
        tipo_cuenta: tercero.tipo_cuenta || '',
        numero_cuenta: tercero.numero_cuenta || '',
      });
    } else {
      setEditId(null);
      setForm(initialForm);
    }
    setDialogOpen(true);
  };

  const [intento, setIntento] = useState(false);
  const handleSave = async () => {
    setIntento(true);
    if (errorTelefono(form.telefono) || errorCorreo(form.email_facturacion)) return;
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/terceros/${editId}`, form);
      } else {
        await api.post('/terceros', form);
      }
      setDialogOpen(false);
      fetchTerceros();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const updates = { [field]: value };

    // Si cambia pais, ajustar tipo_documento y moneda
    if (field === 'pais') {
      const cfg = paisConfig[value];
      if (cfg) {
        updates.tipo_documento = cfg.tipoDoc;
        updates.moneda_default = cfg.moneda;
        updates.regimen_fiscal = '';
      } else {
        updates.tipo_documento = 'ID Fiscal';
        updates.moneda_default = 'USD';
        updates.regimen_fiscal = '';
      }
    }

    setForm(prev => ({ ...prev, ...updates }));
  };

  // Import functions
  const handleOpenImport = async () => {
    setImportOpen(true);
    setImportLoading(true);
    setImportMsg('');
    try {
      const res = await api.get('/integracion/empresas-nara');
      setEmpresasNara(res.data);
    } catch (err) {
      console.error(err);
      setImportMsg('Error al cargar empresas de NARA');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async (emp) => {
    setImporting(emp.id);
    setImportMsg('');
    try {
      await api.post('/integracion/importar-empresa', {
        nara_empresa_id: emp.id,
        company: emp.company,
        address: emp.address,
        city: emp.city,
        country: emp.country,
        tax_id: emp.tax_id,
      });
      setEmpresasNara(prev => prev.map(e =>
        e.id === emp.id ? { ...e, ya_importada: true } : e
      ));
      setImportMsg(`"${emp.company}" importada exitosamente`);
      fetchTerceros();
    } catch (err) {
      setImportMsg(err.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(null);
    }
  };

  const currentPaisConfig = paisConfig[form.pais] || null;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>{terceros.length} registrados</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleOpenImport}
            sx={{ borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
            Importar desde NARA
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
            sx={{ bgcolor: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2d5a87' } }}>
            Nuevo Tercero
          </Button>
        </Box>
      </Box>

      {/* Tabs de filtro por rol */}
      <Tabs value={tabRol} onChange={(e, v) => { setTabRol(v); setLoading(true); }}
        sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13 } }}>
        <Tab label="Todos" />
        <Tab label="Clientes" />
        <Tab label="Proveedores" />
      </Tabs>

      <Paper elevation={0} sx={CONTENEDOR_TABLA}>
        {terceros.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PeopleIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>No hay terceros</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>Importa desde NARA o agrega manualmente</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={tablaAncha(900)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>RAZON SOCIAL</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>ROL</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>DOCUMENTO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>PAIS</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>CIUDAD</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>EMAIL</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>NARA ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">ACCIONES</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {terceros.map((c) => (
                  <TableRow key={c.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{c.razon_social}</Typography>
                      {c.nombre_comercial && c.nombre_comercial !== c.razon_social && (
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{c.nombre_comercial}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {c.es_cliente && (
                          <Chip label="Cliente" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#d1fae5', color: '#059669' }} />
                        )}
                        {c.es_proveedor && (
                          <Chip label="Proveedor" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#ffedd5', color: '#ea580c' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${c.tipo_documento}: ${c.numero_documento || '\u2014'}`} size="small"
                        sx={{ height: 22, fontSize: 11, bgcolor: '#f1f5f9', color: '#475569' }} />
                    </TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: 13 }}>{c.pais || '\u2014'}</TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: 13 }}>{c.ciudad || '\u2014'}</TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: 13 }}>{c.email_facturacion || '\u2014'}</TableCell>
                    <TableCell>
                      {c.nara_empresa_id ? (
                        <Chip label={c.nara_empresa_id} size="small" sx={{ height: 22, fontSize: 11, bgcolor: '#eff6ff', color: '#1e3a5f' }} />
                      ) : '\u2014'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpen(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog Crear/Editar Tercero */}
      <Dialog fullScreen={esMovil} open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>
          {editId ? 'Editar Tercero' : 'Nuevo Tercero'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            {/* Roles */}
            <Box sx={{ gridColumn: { xs: 'auto', sm: 'span 2' }, display: 'flex',
                       gap: 2, alignItems: 'center', flexWrap: 'wrap',
                       bgcolor: '#f8fafc', p: 1.5, borderRadius: 1 }}>
              <Typography sx={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>ROL:</Typography>
              <FormControlLabel
                control={<Checkbox checked={form.es_cliente} onChange={handleChange('es_cliente')} size="small" />}
                label={<Typography sx={{ fontSize: 13 }}>Cliente</Typography>}
              />
              <FormControlLabel
                control={<Checkbox checked={form.es_proveedor} onChange={handleChange('es_proveedor')} size="small" />}
                label={<Typography sx={{ fontSize: 13 }}>Proveedor</Typography>}
              />
            </Box>

            {/* Datos basicos */}
            <TextField label="Razon Social *" value={form.razon_social} onChange={handleChange('razon_social')}
              size="small" sx={{ gridColumn: { xs: 'auto', sm: 'span 2' } }} />
            <TextField label="Nombre Comercial" value={form.nombre_comercial} onChange={handleChange('nombre_comercial')} size="small" />
            <TextField label="NARA Empresa ID" value={form.nara_empresa_id} onChange={handleChange('nara_empresa_id')}
              size="small" placeholder="ej: 4" disabled={!!editId && !!form.nara_empresa_id} />

            {/* Pais - determina campos fiscales */}
            <TextField select label="Pais *" value={form.pais} onChange={handleChange('pais')} size="small">
              {paises.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField label="Ciudad" value={form.ciudad} onChange={handleChange('ciudad')} size="small" />

            {/* Datos fiscales adaptados al pais */}
            <TextField
              label={currentPaisConfig ? `${currentPaisConfig.tipoDocLabel} *` : 'Numero Documento Fiscal *'}
              value={form.numero_documento}
              onChange={handleChange('numero_documento')}
              size="small"
              placeholder={currentPaisConfig ? `Ingresa el ${currentPaisConfig.tipoDocLabel}` : 'Numero de identificacion fiscal'}
            />
            <TextField
              label="Tipo Documento"
              value={form.tipo_documento}
              onChange={handleChange('tipo_documento')}
              size="small"
              disabled={!!currentPaisConfig}
              helperText={currentPaisConfig ? `Asignado por pais: ${currentPaisConfig.tipoDoc}` : ''}
            />

            {/* Regimen fiscal */}
            {currentPaisConfig ? (
              <TextField select label="Regimen Fiscal" value={form.regimen_fiscal}
                onChange={handleChange('regimen_fiscal')} size="small">
                <MenuItem value="">Seleccionar...</MenuItem>
                {currentPaisConfig.regimenes.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            ) : (
              <TextField label="Regimen Fiscal" value={form.regimen_fiscal}
                onChange={handleChange('regimen_fiscal')} size="small" placeholder="Tipo de regimen fiscal" />
            )}

            <TextField label="Moneda" value={form.moneda_default} onChange={handleChange('moneda_default')} size="small" />

            {/* Direccion */}
            <TextField label="Direccion Fiscal" value={form.direccion_fiscal} onChange={handleChange('direccion_fiscal')}
              size="small" sx={{ gridColumn: { xs: 'auto', sm: 'span 2' } }} />
            <TextField label="Departamento / Estado" value={form.departamento} onChange={handleChange('departamento')} size="small" />
            <CampoTelefono etiqueta="Teléfono" valor={form.telefono} mostrarError={intento}
              alCambiar={(v) => setForm({ ...form, telefono: v })} />
            <CampoCorreo etiqueta="Correo de facturación" valor={form.email_facturacion} mostrarError={intento}
              alCambiar={(v) => setForm({ ...form, email_facturacion: v })}
              sx={{ gridColumn: { xs: 'auto', sm: 'span 2' } }} />

            {/* Datos bancarios (visible si es proveedor) */}
            {form.es_proveedor && (
              <>
                <Box sx={{ gridColumn: { xs: 'auto', sm: 'span 2' }, mt: 1, borderTop: '1px solid #e2e8f0', pt: 1.5 }}>
                  <Typography sx={{ color: '#1e3a5f', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Datos Bancarios (Proveedor)
                  </Typography>
                </Box>
                <TextField label="Banco" value={form.banco} onChange={handleChange('banco')} size="small" />
                <TextField select label="Tipo de Cuenta" value={form.tipo_cuenta} onChange={handleChange('tipo_cuenta')} size="small">
                  <MenuItem value="">Seleccionar...</MenuItem>
                  <MenuItem value="ahorros">Ahorros</MenuItem>
                  <MenuItem value="corriente">Corriente</MenuItem>
                </TextField>
                <TextField label="Numero de Cuenta" value={form.numero_cuenta} onChange={handleChange('numero_cuenta')}
                  size="small" sx={{ gridColumn: { xs: 'auto', sm: 'span 2' } }} />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !form.razon_social}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#2d5a87' } }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Importar desde NARA */}
      <Dialog fullScreen={esMovil} open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudDownloadIcon />
          Importar desde NARA Chatbot
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: 13, mb: 2 }}>
            Selecciona las empresas que deseas importar. Los datos basicos se llenaran automaticamente y podras completar la informacion fiscal.
          </Typography>

          {importMsg && (
            <Alert severity={importMsg.includes('Error') ? 'error' : 'success'} sx={{ mb: 2, borderRadius: 1 }}
              onClose={() => setImportMsg('')}>
              {importMsg}
            </Alert>
          )}

          {importLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : empresasNara.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#64748b' }}>No se encontraron empresas en NARA</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {empresasNara.map((emp) => (
                <Paper key={emp.id} elevation={0} sx={{
                  p: 1.5, borderRadius: 1,
                  border: emp.ya_importada ? '1px solid #d1fae5' : '1px solid #e2e8f0',
                  bgcolor: emp.ya_importada ? '#f0fdf4' : '#fff',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                        {emp.company}
                      </Typography>
                      <Chip label={`ID: ${emp.id}`} size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: '#eff6ff', color: '#1e3a5f' }} />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                      {[emp.city, emp.country].filter(Boolean).join(', ')} {emp.tax_id ? `| Doc: ${emp.tax_id}` : ''}
                    </Typography>
                  </Box>
                  {emp.ya_importada ? (
                    <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Importada" size="small"
                      sx={{ height: 24, fontSize: 11, bgcolor: '#d1fae5', color: '#059669', fontWeight: 600 }} />
                  ) : (
                    <Button size="small" variant="outlined" onClick={() => handleImport(emp)}
                      disabled={importing === emp.id}
                      sx={{ minWidth: 80, borderColor: '#1e3a5f', color: '#1e3a5f', textTransform: 'none',
                        fontWeight: 600, fontSize: 12, borderRadius: 1.5 }}>
                      {importing === emp.id ? <CircularProgress size={16} /> : 'Importar'}
                    </Button>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportOpen(false)} sx={{ color: '#64748b' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
